import React, { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Clock, DownloadSimple, FilePdf } from "@phosphor-icons/react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { exportPdf, captureCharts } from "@/components/reports/reportUtils";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const hhmm = (min) => {
  const m = Math.round(Number(min) || 0);
  return `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, "0")}m`;
};

const dayLabel = (iso) =>
  new Date(iso).toLocaleDateString("en-IN", { weekday: "short", day: "2-digit", month: "short" });

const ChartCard = ({ title, children }) => (
  <Card className="p-5 border-slate-200 shadow-none mt-6" data-chart-capture data-chart-title={title}>
    <h3 className="font-semibold text-slate-900 mb-4">{title}</h3>
    {children}
  </Card>
);

const PDF_COLUMNS = [
  { key: "date", label: "Date" },
  { key: "operator_name", label: "Operator" },
  { key: "sessions", label: "Sessions" },
  { key: "span", label: "First / Last", value: (r) => `${r.first_start} - ${r.last_end}` },
  { key: "worked_minutes", label: "Worked", value: (r) => hhmm(r.worked_minutes) },
  { key: "approved_break_minutes", label: "Breaks", value: (r) => hhmm(r.approved_break_minutes) },
  { key: "gap_minutes", label: "Gaps", value: (r) => hhmm(r.gap_minutes) },
  { key: "unaccounted_minutes", label: "Unaccounted idle", value: (r) => hhmm(r.unaccounted_minutes) },
  { key: "utilisation_pct", label: "Utilisation %", value: (r) => `${r.utilisation_pct}%` },
];

export default function IdleTimeReport({ team }) {
  const rootRef = useRef(null);
  const [days, setDays] = useState("7");
  const [operator, setOperator] = useState("all");

  const { data, isLoading, isError } = useQuery({
    queryKey: ["idle-report", days, operator],
    queryFn: async () =>
      (await api.get("/work/idle-report", {
        params: { days: Number(days), operator_id: operator === "all" ? "" : operator },
      })).data,
  });

  const rows = data?.rows || [];

  const exportCsv = () => {
    const head = ["Date", "Operator", "Sessions", "First start", "Last end", "Worked (min)", "Approved breaks (min)", "Gaps between tasks (min)", "Shift (min)", "Unaccounted idle (min)", "Utilisation %"];
    const body = rows.map((r) => [
      r.date, r.operator_name, r.sessions, r.first_start, r.last_end,
      r.worked_minutes, r.approved_break_minutes, r.gap_minutes, r.shift_minutes, r.unaccounted_minutes, r.utilisation_pct,
    ]);
    const csv = [head, ...body].map((line) => line.map((c) => `"${c}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `idle_time_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportPdfIdle = async () => {
    await exportPdf({
      title: "Idle Time Report",
      subtitle: `Last ${days} days · ${rows.length} operator-days · Shift ${data?.shift_start || "—"} to ${data?.shift_end || "—"}`,
      columns: PDF_COLUMNS,
      rows,
      filename: `idle_time_${new Date().toISOString().slice(0, 10)}`,
      charts: await captureCharts(rootRef.current),
    });
  };

  const chartData = rows.map((r) => ({
    name: `${dayLabel(r.date)} · ${r.operator_name}`,
    Worked: Math.round(r.worked_minutes),
    Breaks: Math.round(r.approved_break_minutes),
    Gaps: Math.round(r.gap_minutes),
    "Unaccounted idle": Math.round(r.unaccounted_minutes),
  }));

  return (
    <div data-testid="idle-report" ref={rootRef}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="text-sm text-slate-500">
          Shift {data?.shift_start || "—"} to {data?.shift_end || "—"} ({hhmm(data?.shift_minutes)} a day). Unaccounted idle = shift minus worked minus approved breaks.
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={operator} onValueChange={setOperator}>
            <SelectTrigger className="h-9 w-[190px]" data-testid="idle-operator-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All operators</SelectItem>
              {(team || []).map((t) => (
                <SelectItem key={t.id} value={t.id}>{t.code} · {t.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="h-9 w-[130px]" data-testid="idle-days-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["7", "14", "30"].map((d) => <SelectItem key={d} value={d}>Last {d} days</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={exportCsv} data-testid="idle-export-csv">
            <DownloadSimple className="h-4 w-4 mr-1.5" /> CSV
          </Button>
          <Button variant="outline" onClick={exportPdfIdle} data-testid="idle-export-pdf">
            <FilePdf className="h-4 w-4 mr-1.5" /> PDF
          </Button>
        </div>
      </div>

      {isError ? (
        <div className="py-16 text-center text-sm text-slate-400" data-testid="idle-no-access">
          <Clock className="h-8 w-8 mx-auto mb-2" />
          You do not have access to the idle time report.
        </div>
      ) : isLoading ? (
        <div className="py-16 text-center text-slate-400">Loading idle report...</div>
      ) : rows.length === 0 ? (
        <div className="py-16 text-center text-sm text-slate-400" data-testid="idle-empty">
          <Clock className="h-8 w-8 mx-auto mb-2" />
          No completed work sessions in this period.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            {[
              ["Worked", data.totals.worked_minutes, "text-emerald-700"],
              ["Approved breaks", data.totals.approved_break_minutes, "text-blue-700"],
              ["Gaps between tasks", data.totals.gap_minutes, "text-amber-700"],
              ["Unaccounted idle", data.totals.unaccounted_minutes, "text-red-700"],
            ].map(([label, val, color]) => (
              <div key={label} className="rounded-lg border border-slate-200 p-3">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
                <div className={`text-lg font-bold mt-1 ${color}`} data-testid={`idle-total-${String(label).toLowerCase().replace(/ /g, "-")}`}>
                  {hhmm(val)}
                </div>
              </div>
            ))}
          </div>

          <div className="border border-slate-200 rounded-lg overflow-x-auto">
            <Table>
              <TableHeader className="bg-slate-50">
                <TableRow>
                  <TableHead>Date</TableHead><TableHead>Operator</TableHead>
                  <TableHead className="text-center">Sessions</TableHead>
                  <TableHead className="text-center">First / Last</TableHead>
                  <TableHead className="text-right">Worked</TableHead>
                  <TableHead className="text-right">Breaks</TableHead>
                  <TableHead className="text-right">Gaps</TableHead>
                  <TableHead className="text-right">Unaccounted idle</TableHead>
                  <TableHead className="text-center">Utilisation</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={`${r.date}-${r.operator_id}`} data-testid={`idle-row-${r.date}-${r.operator_code}`}>
                    <TableCell className="whitespace-nowrap">{dayLabel(r.date)}</TableCell>
                    <TableCell className="font-medium">{r.operator_name}</TableCell>
                    <TableCell className="text-center">{r.sessions}</TableCell>
                    <TableCell className="text-center text-slate-500 whitespace-nowrap">{r.first_start} – {r.last_end}</TableCell>
                    <TableCell className="text-right">{hhmm(r.worked_minutes)}</TableCell>
                    <TableCell className="text-right">{hhmm(r.approved_break_minutes)}</TableCell>
                    <TableCell className="text-right text-amber-700">{hhmm(r.gap_minutes)}</TableCell>
                    <TableCell className="text-right font-semibold text-red-700">{hhmm(r.unaccounted_minutes)}</TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline" className={
                        r.utilisation_pct >= 80 ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                          : r.utilisation_pct >= 50 ? "bg-amber-100 text-amber-700 border-amber-200"
                          : "bg-red-100 text-red-700 border-red-200"
                      }>{r.utilisation_pct}%</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <ChartCard title="Worked vs Idle Minutes per Operator-Day">
            <ResponsiveContainer width="100%" height={340}>
              <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 9 }} stroke="#94a3b8" interval={0} angle={-20} textAnchor="end" height={80} />
                <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" label={{ value: "minutes", angle: -90, position: "insideLeft", fontSize: 10, fill: "#94a3b8" }} />
                <Tooltip formatter={(v) => hhmm(v)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="Worked" stackId="a" fill="#16a34a" />
                <Bar dataKey="Breaks" stackId="a" fill="#2563eb" />
                <Bar dataKey="Gaps" stackId="a" fill="#eab308" />
                <Bar dataKey="Unaccounted idle" stackId="a" fill="#dc2626" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </>
      )}
    </div>
  );
}
